import {
    endOfDay,
    getHours,
    getMinutes,
    isEqual,
    roundToNearestMinutes,
    startOfDay,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { DateSpan } from '../../hooks/useSchedulingPreferences/types';

// allows us to compare ends and starts e.g. 2022-03-05T23:59:59.999 and 2022-03-06T00:00:00
export const isEqualWithinMinute = (date1: number | Date, date2: number | Date) =>
    isEqual(
        roundToNearestMinutes(date1, { nearestTo: 5 }),
        roundToNearestMinutes(date2, { nearestTo: 5 })
    );

export const startOfZonedDay = (date: Date | string, timeZone: string) =>
    zonedTimeToUtc(startOfDay(utcToZonedTime(date, timeZone)), timeZone);

export const endOfZonedDay = (date: Date | string, timeZone: string) =>
    zonedTimeToUtc(endOfDay(utcToZonedTime(date, timeZone)), timeZone);

export const withinSameZonedDay = (start: Date | string, end: Date | string, timeZone: string) =>
    !isEqualWithinMinute(endOfZonedDay(start, timeZone), startOfZonedDay(end, timeZone));

const splitTimeIntervalsIntoZonedDays = (intervals: DateSpan[], timeZone: string) =>
    intervals?.reduce<DateSpan[]>((acc, interval) => {
        if (withinSameZonedDay(interval.start, interval.end, timeZone)) {
            return [...acc, interval];
        } else {
            return [
                ...acc,
                { start: new Date(interval.start), end: endOfZonedDay(interval.start, timeZone) },
                { start: startOfZonedDay(interval.end, timeZone), end: new Date(interval.end) },
            ];
        }
    }, []);

const joinAdjacentTimeIntervals = (intervals: DateSpan[], timeZone: string) =>
    intervals
        ?.filter(interval => !isEqualWithinMinute(interval.start, interval.end))
        .reduce<DateSpan[]>((acc, interval) => {
            const previousAdjacentInterval = acc.find(
                ({ end }) =>
                    isEqualWithinMinute(end, interval.start) &&
                    !isEqualWithinMinute(end, endOfZonedDay(end, timeZone))
            );
            if (previousAdjacentInterval) {
                previousAdjacentInterval.end = interval.end;
                return acc;
            } else {
                return [...acc, interval];
            }
        }, []);

export const groupTimeIntervalsByZonedDay = (intervals: DateSpan[], timeZone: string) => {
    return joinAdjacentTimeIntervals(
        splitTimeIntervalsIntoZonedDays(intervals, timeZone),
        timeZone
    );
};

export const getTotalMinutesOfZonedDay = (date: Date, timeZone: string) =>
    getHours(utcToZonedTime(date, timeZone)) * 60 + getMinutes(utcToZonedTime(date, timeZone));
